import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { canViewStockAlerts } from "@/lib/roleUtils";
import { api } from "@/lib/apiClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, ShoppingCart, Package, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

interface LowStockItem {
  id: string;
  name: string;
  code?: string | null;
  quantity: number;
  min_stock?: number | null;
  unit?: string | null;
  type: "raw" | "prepreg";
}

/**
 * Aviso para el departamento de Compras: materias primas y preimpregnados 
 * que están en o por debajo de su stock mínimo de seguridad.
 */
export function LowStockAlerts() {
  const { userRole } = useAuth();
  
  // Solo mostrar a roles autorizados
  if (!canViewStockAlerts(userRole)) {
    return null;
  }

  const navigate = useNavigate();

  // Query para materias primas
  const { data: lowStockRaw } = useQuery({
    queryKey: ["low-stock-raw-materials"],
    queryFn: async () => {
      const { data, error } = await api
        .from("raw_materials")
        .select("id, name, code, quantity, min_stock, unit, supplier")
        .eq("is_active", true);
      if (error) throw error;
      return (data || [])
        .filter((m: any) => (m.min_stock || 0) > 0 && m.quantity <= (m.min_stock || 0))
        .map((m: any) => ({ ...m, type: "raw" as const }));
    },
    refetchInterval: 60000,
  });

  // Query para preimpregnados
  const { data: lowStockPrepreg } = useQuery({
    queryKey: ["low-stock-prepreg-materials"],
    queryFn: async () => {
      const { data, error } = await api
        .from("material_inventory")
        .select("id, name, code, current_stock, min_stock, unit, supplier")
        .eq("is_active", true);
      if (error) throw error;
      return (data || [])
        .filter((m: any) => (m.min_stock || 0) > 0 && m.current_stock <= (m.min_stock || 0))
        .map((m: any) => ({ 
          ...m, 
          quantity: m.current_stock,
          type: "prepreg" as const 
        }));
    },
    refetchInterval: 60000,
  });

  // Combinar y ordenar
  const allLowStock: LowStockItem[] = [
    ...(lowStockRaw || []),
    ...(lowStockPrepreg || []),
  ].sort((a, b) => a.name.localeCompare(b.name));

  if (allLowStock.length === 0) {
    return null;
  }

  const rawCount = (lowStockRaw || []).length;
  const prepregCount = (lowStockPrepreg || []).length;

  return (
    <Card className="border-amber-500/30 bg-amber-500/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
          <AlertTriangle className="w-5 h-5" />
          Compras: materiales bajo stock mínimo ({allLowStock.length})
        </CardTitle>
        <div className="flex gap-2 mt-2">
          {prepregCount > 0 && (
            <Badge variant="outline" className="text-xs">
              <Package className="w-3 h-3 mr-1" />
              {prepregCount} preimpregnados
            </Badge>
          )}
          {rawCount > 0 && (
            <Badge variant="outline" className="text-xs">
              <ShoppingCart className="w-3 h-3 mr-1" />
              {rawCount} materias primas
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {allLowStock.map((m) => (
          <div
            key={m.id}
            className="flex items-center justify-between p-3 rounded-lg bg-background border"
          >
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium">{m.name}</span>
                {m.code && (
                  <span className="text-sm text-muted-foreground">({m.code})</span>
                )}
                <Badge className={m.type === "prepreg" 
                  ? "bg-blue-500/10 text-blue-600 border border-blue-500/20"
                  : "bg-amber-500/10 text-amber-600 border border-amber-500/20"
                }>
                  {m.type === "prepreg" ? "Preimpregnado" : "Materia prima"}
                </Badge>
              </div>
              <div className="text-sm text-muted-foreground">
                Stock actual:{" "}
                <span className="font-medium text-amber-600">
                  {m.quantity} {m.unit}
                </span>{" "}
                · mínimo: {m.min_stock} {m.unit}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/materials")}
              >
                {m.type === "prepreg" ? <Package className="w-4 h-4" /> : <ShoppingCart className="w-4 h-4" />}
              </Button>
              <Button
                variant="default"
                size="sm"
                className="gap-1"
                onClick={() => navigate(`/billing/supplier-orders?create=true&supplier=${encodeURIComponent(m.supplier || '')}&material_id=${m.id}`)}
              >
                <Truck className="w-4 h-4" />
                Pedir
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
